import React, { useState } from 'react';
import { 
  Send, 
  CheckCircle2, 
  User, 
  Phone, 
  Mail, 
  FileText, 
  MessageSquare, 
  Sparkles,
  RotateCcw,
  ShieldCheck
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const ContactForm = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [selectedTopic, setSelectedTopic] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const topics = [
    { 
      id: 'cataract', 
      label: language === 'mr' ? '👁️ मोतीबिंदू तपासणी' : (language === 'kn' ? '👁️ ಮೋತಿಬಿಂದು ತಪಾಸಣೆ' : '👁️ Cataract Surgery') 
    },
    { 
      id: 'lasik', 
      label: language === 'mr' ? '⚡ लेझर / नंबर घालवणे' : (language === 'kn' ? '⚡ ಲೇಸರ್ ಚಿಕಿತ್ಸೆ' : '⚡ Lasik / Specs Removal') 
    },
    { 
      id: 'opd', 
      label: language === 'mr' ? '🩺 सामान्य नेत्र तपासणी' : (language === 'kn' ? '🩺 ಸಾಮಾನ್ಯ ಕಣ್ಣಿನ ತಪಾಸಣೆ' : '🩺 General Eye OPD') 
    },
    { 
      id: 'emergency', 
      label: language === 'mr' ? '🚨 तातडीची सेवा' : (language === 'kn' ? '🚨 ತುರ್ತು ಸೇವೆ' : '🚨 Urgent Eye Care') 
    },
    { 
      id: 'general', 
      label: language === 'mr' ? '💬 इतर चौकशी' : (language === 'kn' ? '💬 ಇತರ ವಿಚಾರಣೆ' : '💬 General Enquiry') 
    }
  ];

  const handleTopicSelect = (topic) => {
    setSelectedTopic(topic.id);
    setFormData(prev => ({ ...prev, subject: topic.label.replace(/^[^\s]+\s/, '') }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
    }, 700);
  };

  const handleReset = () => {
    setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    setSelectedTopic('');
    setIsSuccess(false);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="bg-white rounded-3xl p-6 sm:p-8 lg:p-10 shadow-xl border border-slate-200/90 relative overflow-hidden">
      {/* Top Accent Gradient Bar */}
      <div className="absolute top-0 inset-x-0 h-1.5 bg-gradient-to-r from-teal via-clinical to-amber-400"></div>

      {/* Decorative background glow accents */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-clinical/10 via-teal/5 to-transparent rounded-bl-full pointer-events-none"></div>
      <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-clinical/5 rounded-full blur-2xl pointer-events-none"></div>

      <AnimatePresence mode="wait">
        {isSuccess ? (
          <motion.div
            key="success-message"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.4 }}
            className="py-12 px-4 text-center max-w-lg mx-auto"
          >
            <div className="w-20 h-20 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner ring-8 ring-emerald-50/50">
              <CheckCircle2 size={44} strokeWidth={2.2} />
            </div>

            <h3 className="text-2xl sm:text-3xl font-extrabold text-navy mb-3">
              {language === 'mr' ? 'संदेश यशस्वीरित्या प्राप्त झाला!' : (language === 'kn' ? 'ಸಂದೇಶ ಯಶಸ್ವಿಯಾಗಿ ತಲುಪಿದೆ!' : 'Message Sent Successfully!')}
            </h3>

            <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-6">
              {language === 'mr' 
                ? `धन्यवाद ${formData.name ? formData.name : ''}! आमची वैद्यकीय टीम लवकरच ${formData.phone ? formData.phone : ''} वर आपल्याशी संपर्क साधेल.`
                : (language === 'kn'
                  ? `ಧನ್ಯವಾದಗಳು! ನಮ್ಮ ವೈದ್ಯಕೀಯ ತಂಡ ಶೀಘ್ರದಲ್ಲೇ ನಿಮ್ಮೊಂದಿಗೆ ಸಂಪರ್ಕ ಸಾಧಿಸಲಿದೆ.`
                  : `Thank you ${formData.name || ''}! Our medical coordination desk will review your inquiry and connect with you shortly.`
                )
              }
            </p>

            <div className="bg-emerald-50/60 border border-emerald-100 rounded-2xl p-4 mb-8 text-xs sm:text-sm text-emerald-800 flex items-center justify-center gap-2">
              <Sparkles size={16} className="text-emerald-600 shrink-0" />
              <span>
                {language === 'mr' 
                  ? 'तातडीच्या मदतीसाठी आपण थेट कॉल किंवा व्हॉट्सॲप देखील करू शकता.'
                  : 'For immediate assistance, feel free to call our direct helpline.'
                }
              </span>
            </div>

            <button
              onClick={handleReset}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-navy hover:bg-clinical text-white font-semibold text-sm transition-all shadow-md hover:shadow-lg"
            >
              <RotateCcw size={16} />
              <span>{language === 'mr' ? 'दुसरा संदेश पाठवा' : (language === 'kn' ? 'ಮತ್ತೊಂದು ಸಂದೇಶ ಕಳುಹಿಸಿ' : 'Send Another Message')}</span>
            </button>
          </motion.div>
        ) : (
          <motion.form 
            key="contact-form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onSubmit={handleSubmit} 
            className="space-y-6 relative z-10"
          >
            {/* Form Header */}
            <div>
              <div className="flex flex-wrap items-center gap-2.5 mb-2">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-clinical/10 text-clinical text-xs font-bold tracking-wide uppercase">
                  <Sparkles size={13} />
                  <span>{language === 'mr' ? 'ऑनलाईन संपर्क' : (language === 'kn' ? 'ಆನ್‌ಲೈನ್ ವಿಚಾರಣೆ' : 'Direct Assistance')}</span>
                </span>
                <span className="text-xs text-emerald-600 font-semibold flex items-center gap-1.5 bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-200/60">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                  <span>{language === 'mr' ? 'त्वरित प्रतिसाद (< १५ मिनिटे)' : (language === 'kn' ? 'ತ್ವರಿತ ಪ್ರತಿಕ್ರಿಯೆ' : 'Quick Response (< 15 mins)')}</span>
                </span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-extrabold text-navy">
                {t('contactSection.getInTouch')}
              </h3>
              <p className="text-gray-500 text-xs sm:text-sm mt-1">
                {language === 'mr' 
                  ? 'खालील फॉर्म भरा किंवा आपल्या आवश्यकतेनुसार विषय निवडा. आमची टीम लगेच संपर्क करेल.' 
                  : 'Choose a topic or fill in your details below to hear directly from our ophthalmology desk.'
                }
              </p>
            </div>

            {/* Topic Chips */}
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2.5">
                {language === 'mr' ? 'चौकशीचा विषय निवडा:' : (language === 'kn' ? 'ವಿಷಯವನ್ನು ಆಯ್ಕೆಮಾಡಿ:' : 'Select Topic (Optional):')}
              </label>
              <div className="flex flex-wrap gap-2">
                {topics.map((topic) => {
                  const isActive = selectedTopic === topic.id;
                  return (
                    <button
                      key={topic.id}
                      type="button"
                      onClick={() => handleTopicSelect(topic)}
                      className={`text-xs font-semibold px-3.5 py-2 rounded-xl transition-all duration-200 cursor-pointer border ${
                        isActive
                          ? 'bg-gradient-to-r from-navy to-clinical text-white border-clinical shadow-md shadow-clinical/20 scale-102'
                          : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100 hover:text-navy hover:border-slate-300'
                      }`}
                    >
                      {topic.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Inputs: Name & Phone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label htmlFor="name" className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">
                  {t('contactSection.name')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
                    <User size={18} />
                  </div>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50/60 hover:bg-white focus:bg-white focus:ring-2 focus:ring-teal/20 focus:border-teal outline-none transition-all text-sm text-gray-800 placeholder-gray-400 font-medium"
                    placeholder={t('contactSection.namePlaceholder')}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">
                  {t('contactSection.phoneNumber')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
                    <Phone size={18} />
                  </div>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50/60 hover:bg-white focus:bg-white focus:ring-2 focus:ring-teal/20 focus:border-teal outline-none transition-all text-sm text-gray-800 placeholder-gray-400 font-medium"
                    placeholder={t('contactSection.phonePlaceholder')}
                  />
                </div>
              </div>
            </div>
            
            {/* Inputs: Email & Subject */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">
                  {t('contactSection.emailAddress')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
                    <Mail size={18} />
                  </div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50/60 hover:bg-white focus:bg-white focus:ring-2 focus:ring-teal/20 focus:border-teal outline-none transition-all text-sm text-gray-800 placeholder-gray-400 font-medium"
                    placeholder={t('contactSection.emailPlaceholder')}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">
                  {t('contactSection.subject')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
                    <FileText size={18} />
                  </div>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50/60 hover:bg-white focus:bg-white focus:ring-2 focus:ring-teal/20 focus:border-teal outline-none transition-all text-sm text-gray-800 placeholder-gray-400 font-medium"
                    placeholder={t('contactSection.subjectPlaceholder')}
                  />
                </div>
              </div>
            </div>

            {/* Textarea: Message */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="message" className="block text-xs font-bold uppercase tracking-wider text-gray-700">
                  {t('contactSection.message')} <span className="text-red-500">*</span>
                </label>
                <span className="text-[11px] text-gray-400">
                  {formData.message.length > 0 ? `${formData.message.length} characters` : ''}
                </span>
              </div>
              <div className="relative">
                <div className="absolute top-3.5 left-3.5 pointer-events-none text-gray-400">
                  <MessageSquare size={18} />
                </div>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50/60 hover:bg-white focus:bg-white focus:ring-2 focus:ring-teal/20 focus:border-teal outline-none transition-all resize-none text-sm text-gray-800 placeholder-gray-400 font-medium leading-relaxed"
                  placeholder={t('contactSection.messagePlaceholder')}
                ></textarea>
              </div>
            </div>

            {/* Privacy note & Submit Button */}
            <div className="space-y-4 pt-2">
              <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                type="submit"
                disabled={isSubmitting}
                className={`w-full bg-gradient-to-r from-navy via-clinical to-teal hover:from-teal hover:to-navy text-white py-4 px-8 rounded-xl font-bold transition-all shadow-lg shadow-clinical/20 flex items-center justify-center space-x-2 text-base cursor-pointer ${
                  isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting ? (
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>{language === 'mr' ? 'पाठवत आहे...' : 'Sending message...'}</span>
                  </div>
                ) : (
                  <>
                    <span>{t('contactSection.submitEnquiry')}</span>
                    <Send size={18} className="transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </motion.button>

              <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
                <ShieldCheck size={15} className="text-teal" />
                <span>
                  {language === 'mr' 
                    ? 'आपली माहिती १००% सुरक्षित व गोपनीय ठेवली जाते.' 
                    : 'Your information is confidential and used only for healthcare consultation.'
                  }
                </span>
              </div>

              {/* Fast Direct Action Links Bar */}
              <div className="pt-3 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-500">
                <span className="font-semibold text-navy flex items-center gap-1.5">
                  <Sparkles size={13} className="text-amber-500" />
                  <span>{language === 'mr' ? 'त्वरित सल्ला हवा आहे?' : 'Need faster response?'}</span>
                </span>
                <div className="flex items-center gap-2">
                  <a
                    href="https://wa.me/919876543210"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-600 hover:text-white font-bold transition-colors"
                  >
                    <span>WhatsApp</span>
                  </a>
                  <a
                    href="tel:+919876543210"
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-50 text-clinical hover:bg-clinical hover:text-white font-bold transition-colors"
                  >
                    <span>{language === 'mr' ? 'थेट कॉल' : 'Call Desk'}</span>
                  </a>
                </div>
              </div>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ContactForm;
